import React, { ReactNode } from 'react';
import { Button } from 'antd';

// W3C Trace Context (`traceparent: version-trace_id-parent_id-trace_flags`), e.g.
// 00-4bf92f3577b34da6a3ce929d0e0e4736-00f067aa0ba902b7-01
//
// The trace id (32 hex, a UUID without dashes) is the correlation id: the backends continue the trace,
// so the same value appears in their logs, in the APM trace and in the error reference shown to users.

export interface TraceContext {
  version: string;
  traceId: string;
  parentSpanId: string;
  traceFlags: string;
}

const TRACEPARENT_PATTERN = /^([0-9a-f]{2})-([0-9a-f]{32})-([0-9a-f]{16})-([0-9a-f]{2})(-.*)?$/;
const ALL_ZEROS = /^0+$/;

/** Returns the parsed context, or undefined for a missing or invalid traceparent (spec rules below). */
export const parseTraceparent = (value: unknown): TraceContext | undefined => {
  if (typeof value !== 'string') return undefined;

  const match = TRACEPARENT_PATTERN.exec(value.trim());
  if (!match) return undefined;

  const [, version, traceId, parentSpanId, traceFlags, extra] = match;
  if (version === 'ff') return undefined; // reserved, never valid
  if (version === '00' && extra) return undefined; // version 00 has exactly four fields
  if (ALL_ZEROS.test(traceId) || ALL_ZEROS.test(parentSpanId)) return undefined; // all-zero ids are invalid

  return { version, traceId, parentSpanId, traceFlags };
};

const randomHex = (byteCount: number): string => {
  const bytes = new Uint8Array(byteCount);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const randomNonZeroHex = (byteCount: number): string => {
  let hex = randomHex(byteCount);
  while (ALL_ZEROS.test(hex)) hex = randomHex(byteCount);
  return hex;
};

/** A random UUID v4 as 32 lowercase hex (no dashes): a valid W3C trace id, never all zeros. */
const randomUuidHex = (): string => {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // RFC 4122 variant
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * The traceparent to send. With a valid `incoming` context the trace continues: same trace id and
 * flags, new span id. A missing or malformed one starts a new root trace whose trace id is a UUID v4.
 */
export const createTraceparent = (incoming?: unknown): string => {
  const parent = parseTraceparent(incoming);
  return `00-${parent?.traceId ?? randomUuidHex()}-${randomNonZeroHex(8)}-${parent?.traceFlags ?? '01'}`;
};

/**
 * Request interceptor: a valid traceparent already on the request (a retry) is kept; otherwise a new trace
 * starts. X-Correlation-Id carries the same UUID as the trace id, so logs, APM and the error reference share one id.
 */
export const addTraceparent = <T extends { headers?: any }>(config: T): T => {
  if (config.headers) {
    if (!parseTraceparent(config.headers.traceparent)) {
      config.headers.traceparent = createTraceparent();
    }
    config.headers['X-Correlation-Id'] = parseTraceparent(config.headers.traceparent)?.traceId;
  }
  return config;
};

const readHeader = (headers: any, name: string): string | undefined => {
  const value = headers?.get?.(name) ?? headers?.[name];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
};

/**
 * The trace id this app generated and sent (32 hex, a UUID without dashes): the backends continue that trace,
 * so it is the id to show. The response header carries only its lower 64 bits as a decimal number; that header
 * and the body id are the fallback for requests that carried no traceparent.
 */
export const getCorrelationId = (error: any): string | undefined => {
  const body = error?.response?.data;
  const bodyId = typeof body?.correlationId === 'string' && body.correlationId.trim() ? body.correlationId : undefined;
  const sent = parseTraceparent(readHeader(error?.config?.headers, 'traceparent'))?.traceId;

  return sent || readHeader(error?.response?.headers, 'x-correlation-id') || bodyId;
};

/** Error toast text with a copyable reference, so a user can quote it to support. */
export const formatApiError = (error: unknown, fallback: string): ReactNode => {
  const correlationId = getCorrelationId(error);
  if (!correlationId) return fallback;

  return (
    <span>
      {fallback} Reference: {correlationId}{' '}
      <Button
        size="small"
        type="text"
        aria-label="Copy reference"
        onClick={() => void navigator.clipboard?.writeText(correlationId)}
      >
        Copy
      </Button>
    </span>
  );
};
